import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Activity, AppWindow, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { AppIcon } from '../../components/AppIcon';
import { ErrorState, LoadingState, Spinner, useToast } from '../../components/Feedback';
import { appColor } from '../../lib/utils';
import type { AdminOverview as Overview } from '../../types';

export function AdminOverviewPage() {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['admin-overview'],
        queryFn: () => api.get<Overview>('/api/admin/overview'),
    });

    const queryClient = useQueryClient();
    const { notify } = useToast();

    const healthCheck = useMutation({
        mutationFn: () =>
            api.post<{
                checked: number;
                down: number;
                downInstances?: { application: string; label: string; url: string }[];
            }>('/api/admin/health-check'),
        onSuccess: (result) => {
            // Nommer plutot que compter : « 1 injoignable » n'apprend rien tant qu'on
            // ignore laquelle, et l'administrateur devait ouvrir les fiches une par une.
            if (result.down === 0) {
                notify(`${result.checked} adresses contrôlées, toutes répondent.`, 'success');
            } else {
                const noms = (result.downInstances ?? [])
                    .map((i) => `${i.application} (${i.label})`)
                    .join(', ');
                notify(
                    `${result.checked} adresses contrôlées. Sans réponse : ${noms || result.down}.`,
                    'error'
                );
            }
            void queryClient.invalidateQueries({ queryKey: ['catalog'] });
        },
        onError: () => notify('Le contrôle d’état a échoué.', 'error'),
    });

    if (isLoading) return <LoadingState />;
    if (isError || !data) {
        return (
            <ErrorState
                message={error instanceof Error ? error.message : 'Chargement impossible.'}
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard
                    icon={<AppWindow className="w-4 h-4" aria-hidden />}
                    label="Applications"
                    value={data.applications.total}
                    hint={`${data.applications.active} en service · ${data.applications.openAccess} ouvertes à tous`}
                />
                <StatCard
                    icon={<Users className="w-4 h-4" aria-hidden />}
                    label="Utilisateurs actifs"
                    value={data.users.total}
                    hint={`${data.users.admins} administrateur${data.users.admins > 1 ? 's' : ''}`}
                />
                <StatCard
                    icon={<ShieldCheck className="w-4 h-4" aria-hidden />}
                    label="Droits attribués"
                    value={data.grants}
                    hint="Hors droits hérités d’un groupe Entra"
                />
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <section className="lg:col-span-2 card">
                    <h2 className="px-5 py-3 text-sm font-semibold text-[var(--color-text-secondary)] border-b border-[var(--color-border)]">
                        Applications les plus ouvertes sur 30 jours
                    </h2>

                    {data.topApplications.every((a) => a.visits === 0) ? (
                        <p className="px-5 py-6 text-sm text-[var(--color-text-muted)]">
                            Aucune ouverture enregistrée pour l’instant.
                        </p>
                    ) : (
                        <ul className="divide-y divide-[var(--color-border)]">
                            {data.topApplications.map((application) => {
                                const max = Math.max(
                                    ...data.topApplications.map((a) => a.visits),
                                    1
                                );
                                return (
                                    <li
                                        key={application.id}
                                        className="flex items-center gap-3 px-5 py-3"
                                    >
                                        <span
                                            className="app-swatch flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                                            style={appColor(application.color)}
                                            aria-hidden
                                        >
                                            <AppIcon name={application.icon} className="w-4 h-4" />
                                        </span>

                                        <Link
                                            to={`/applications/${application.slug}`}
                                            className="text-sm font-medium text-[var(--color-text)] hover:text-[var(--color-primary)] w-48 truncate"
                                        >
                                            {application.name}
                                        </Link>

                                        <div className="flex-1 h-2 rounded-full bg-[var(--color-bg-hover)] overflow-hidden">
                                            <div
                                                className="app-bar h-full rounded-full"
                                                style={{
                                                    width: `${(application.visits / max) * 100}%`,
                                                    ...appColor(application.color),
                                                }}
                                            />
                                        </div>

                                        <span className="text-sm text-[var(--color-text-secondary)] tabular-nums w-10 text-right">
                                            {application.visits}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                <section className="card card-padded space-y-3">
                    <h2 className="text-sm font-semibold text-[var(--color-text-secondary)]">
                        Opérations
                    </h2>

                    {/*
                     * La synchronisation ne se déclenche plus d'ici. Elle vit
                     * dans l'onglet Utilisateurs, avec son compte rendu : deux
                     * boutons pour une même opération, dont un seul montre ce
                     * qui a été retenu et écarté, seraient un piège.
                     */}
                    <Link to="/admin/users" className="btn btn-secondary w-full justify-start">
                        <RefreshCw className="w-4 h-4" aria-hidden />
                        Synchroniser l’annuaire Entra ID
                    </Link>
                    <p className="text-xs text-[var(--color-text-muted)] -mt-1">
                        Se déclenche depuis l’onglet Utilisateurs, où le compte rendu dit qui a
                        été retenu, qui a été écarté et pour quel motif.
                    </p>

                    <button
                        type="button"
                        onClick={() => healthCheck.mutate()}
                        disabled={healthCheck.isPending}
                        className="btn btn-secondary w-full justify-start"
                    >
                        {healthCheck.isPending ? (
                            <Spinner className="w-4 h-4" />
                        ) : (
                            <Activity className="w-4 h-4" aria-hidden />
                        )}
                        Contrôler l’état des applications
                    </button>
                    <p className="text-xs text-[var(--color-text-muted)] -mt-1">
                        Interroge les adresses dotées d’une URL de contrôle. Les applications sur
                        réseau interne ne sont sondées que si le Hub peut les joindre.
                    </p>
                </section>
            </div>
        </div>
    );
}

interface StatCardProps {
    icon: React.ReactNode;
    label: string;
    value: number;
    hint?: string;
    highlight?: boolean;
}

function StatCard({ icon, label, value, hint, highlight }: StatCardProps) {
    return (
        <div
            className="card p-5"
            style={highlight ? { borderColor: 'var(--color-warning)' } : undefined}
        >
            <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
                {icon}
                <span className="text-sm font-medium">{label}</span>
            </div>
            <p className="mt-2 text-2xl font-bold text-[var(--color-text)] tabular-nums">{value}</p>
            {hint && <p className="mt-1 text-xs text-[var(--color-text-muted)]">{hint}</p>}
        </div>
    );
}
